import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";

import { useAuth, startSessionWatcher } from "@/store/auth";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Contacts from "@/pages/Contacts";
import Devices from "@/pages/Devices";
import DeviceDetail from "@/pages/DeviceDetail";
import DeviceTypes from "@/pages/DeviceTypes";
import Gateways from "@/pages/Gateways";
import GatewayDetail from "@/pages/GatewayDetail";
import Messages from "@/pages/Messages";
import Callbacks from "@/pages/Callbacks";
import EnvelopeRules from "@/pages/EnvelopeRules";
import MqttTopics from "@/pages/MqttTopics";
import { OutputDestinations, ParserPipelines } from "@/pages/OutputsAndPipelines";
import System from "@/pages/System";
import Users from "@/pages/Users";
import WhoAmI from "@/pages/WhoAmI";
import AuditLog from "@/pages/AuditLog";
import { can } from "@/lib/rbac";

const qc = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 0 } },
});

function Protected({ children }) {
  const isAuth = useAuth((s) => s.isAuthenticated());
  const location = useLocation();
  const navigate = useNavigate();

  // Kick off the pre-expiry watcher once, hoisted to the app level.
  useEffect(() => {
    return startSessionWatcher(() => {
      toast.error("Your session has expired — please sign in again.");
      navigate("/login", { replace: true });
    });
  }, [navigate]);

  if (!isAuth) return <Navigate to="/login" state={{ from: location }} replace />;
  return <Layout>{children}</Layout>;
}

// AdminOnly — hard-gates a route to the "admin" role. Non-admins are
// silently bounced to Dashboard so they can't even see the URL exist.
function AdminOnly({ children }) {
  const role = useAuth((s) => s.role);
  if (!can(role, "read:users")) return <Navigate to="/" replace />;
  return children;
}

// RequireCap — generic route guard for arbitrary capabilities. Use this when
// the sidebar hides the entry but a determined user could still deep-link
// into the page (e.g. bookmarking /audit as an operator).
function RequireCap({ cap, children }) {
  const role = useAuth((s) => s.role);
  if (!can(role, cap)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/clients" element={<Protected><Clients /></Protected>} />
          <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
          <Route path="/contacts" element={<Protected><Contacts /></Protected>} />
          <Route path="/devices" element={<Protected><Devices /></Protected>} />
          <Route path="/devices/:id" element={<Protected><DeviceDetail /></Protected>} />
          <Route path="/device-types" element={<Protected><DeviceTypes /></Protected>} />
          <Route path="/gateways" element={<Protected><Gateways /></Protected>} />
          <Route path="/gateways/:id" element={<Protected><GatewayDetail /></Protected>} />
          <Route path="/messages" element={<Protected><Messages /></Protected>} />
          <Route path="/callbacks" element={<Protected><Callbacks /></Protected>} />
          <Route path="/envelope-rules" element={<Protected><EnvelopeRules /></Protected>} />
          <Route path="/mqtt-topics" element={<Protected><MqttTopics /></Protected>} />
          <Route path="/outputs" element={<Protected><OutputDestinations /></Protected>} />
          <Route path="/pipelines" element={<Protected><ParserPipelines /></Protected>} />
          <Route path="/users" element={<Protected><AdminOnly><Users /></AdminOnly></Protected>} />
          <Route path="/audit" element={<Protected><RequireCap cap="read:audit"><AuditLog /></RequireCap></Protected>} />
          <Route path="/whoami" element={<Protected><WhoAmI /></Protected>} />
          <Route path="/system" element={<Protected><System /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton duration={4500} />
    </QueryClientProvider>
  );
}
